import React, {useState} from 'react';
import {Box} from '@mui/material';
import {Icon, designSystemToken} from '@lightricks/react-design-system';
import styles from './StarRating.module.scss';

const DEFAULT_STAR_COUNT = 5;

export interface StarRatingProps {
  value: number | null;
  onChange: (value: number) => void;
  starCount?: number;
}

function StarRating({
  value,
  onChange,
  starCount = DEFAULT_STAR_COUNT,
}: StarRatingProps) {
  // While hovering (or keyboard-focusing) a star, preview the fill for that
  // star and every star before it; fall back to the committed value on leave.
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const displayValue = hoverValue ?? value;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        // Space stars via gap (not button padding) so the glyphs sit flush
        // with the edges of this box and align with surrounding content.
        gap: 1,
      }}
      role="group"
      aria-label="Rate 1 to 5 stars"
      onMouseLeave={() => setHoverValue(null)}
      onBlur={() => setHoverValue(null)}
    >
      {Array.from({length: starCount}, (_, i) => {
        const starValue = i + 1;
        const filled = displayValue !== null && starValue <= displayValue;
        return (
          <button
            key={starValue}
            type="button"
            onClick={() => onChange(starValue)}
            onMouseEnter={() => setHoverValue(starValue)}
            onFocus={() => setHoverValue(starValue)}
            className={styles.starButton}
            aria-label={`${starValue} star${starValue === 1 ? '' : 's'}`}
            aria-pressed={value !== null && starValue <= value}
          >
            <Icon
              name={filled ? 'Navigation-Star-Fill' : 'Navigation-Star-Line'}
              size="medium"
              appearance={filled ? 'primary' : 'neutral'}
              color={designSystemToken('semantic.bg.warning')}
            />
          </button>
        );
      })}
    </Box>
  );
}

export default StarRating;
